import { useEffect, useMemo, useState } from 'react';
import { iniciais } from '../../acesso';
import { ErroDeArea } from '../../design/Estados';
import { lerSemana, reais as reaisN, reaisCurto } from '../../premiacao';
import type { ChequeDaPessoa } from '../../premiacao';
import { reais } from '../../produtos';
import { foraDaConta, hora, lerRanking, lerVendasDaEquipe } from '../../vendas';
import { linkDasVendas } from '../inovapay/link-das-vendas';
import type { LinhaDoRanking, VendasDaEquipe } from '../../vendas';
import './vendas.css';
import { semanaDoDia } from '../../design/semana';

/**
 * TELA 10 (parte C) — PERFIL COMERCIAL DE UM BALCONISTA (visão do admin). Desempenho comercial e
 * nada além disso: sem dado pessoal, sem RH. Junta o que já existe — as vendas de 30 dias contra a
 * média da equipe, a premiação da semana (o cheque) e a posição no ranking. A meta é a da equipe
 * (a do 1.0: uma só para todos); meta por pessoa seria regra nova e não entra sem o dono.
 */

function hojeBrasilia(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}
const semanaDe = semanaDoDia;

export function PerfilComercial({ accountId }: { accountId: string }) {
  const hoje = hojeBrasilia();
  const semana = semanaDe(hoje);
  const trinta = new Date(Date.parse(`${hoje}T12:00:00Z`) - 29 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  const [pessoa, setPessoa] = useState<VendasDaEquipe | null>(null);
  const [equipe, setEquipe] = useState<VendasDaEquipe | null>(null);
  const [cheque, setCheque] = useState<ChequeDaPessoa | null | undefined>(undefined);
  const [posicao, setPosicao] = useState<{ linha: LinhaDoRanking | null; meta: number } | null>(
    null,
  );
  const [erro, setErro] = useState(false);

  useEffect(() => {
    setErro(false);
    void Promise.all([
      lerVendasDaEquipe({ de: trinta, ate: hoje, pessoa: accountId }),
      lerVendasDaEquipe({ de: trinta, ate: hoje }),
      lerSemana(semana.de, semana.ate),
      lerRanking('semana'),
    ]).then(([p, e, s, r]) => {
      if (!p.ok || !e.ok) {
        setErro(true);
        return;
      }
      setPessoa(p.dados);
      setEquipe(e.dados);
      setCheque(s.ok ? (s.dados.pessoas.find((x) => x.pessoaId === accountId) ?? null) : null);
      if (r.ok)
        setPosicao({
          linha: r.dados.linhas.find((l) => l.accountId === accountId) ?? null,
          meta: r.dados.metaSemanal,
        });
    });
  }, [accountId, hoje, trinta, semana.de, semana.ate]);

  const nome = pessoa?.vendas[0]?.balconista ?? posicao?.linha?.nome ?? '';
  const codigo = pessoa?.vendas[0]?.codigoBalconista ?? posicao?.linha?.codigo ?? '';
  const naSemana = useMemo(
    () => (pessoa?.vendas ?? []).filter((v) => v.dia >= semana.de && v.dia <= semana.ate),
    [pessoa, semana.de, semana.ate],
  );
  const validas = naSemana.filter((v) => !foraDaConta(v.status));
  const itensSemana = validas.reduce((s, v) => s + v.quantidade, 0);
  const valorSemana =
    validas.reduce((s, v) => s + Math.round(Number(v.precoUnitario) * 100) * v.quantidade, 0) / 100;
  const canceladas = naSemana.filter((v) => v.status === 'cancelada').length;
  const ativos = equipe?.resumo.balconistasAtivos || 1;
  const metaPct =
    posicao?.linha && posicao.meta > 0
      ? Math.round((posicao.linha.itens / posicao.meta) * 100)
      : null;

  if (erro) return <ErroDeArea oQue="o perfil" aoTentar={() => window.location.reload()} />;

  return (
    <section className="vd pcm">
      <div className="pcm-cabeca">
        <span className="pcm-avatar">{nome ? iniciais(nome) : ''}</span>
        <div className="pcm-quem">
          <div className="pcm-nome">{nome || 'Carregando…'}</div>
          <div className="pcm-sub">Balconista · cód {codigo || '—'} · Rede Inova · Muriaé</div>
        </div>
        <div className="pcm-numeros">
          <div>
            <span>POSIÇÃO</span>
            <strong>{posicao?.linha ? `${String(posicao.linha.posicao)}º` : '—'}</strong>
          </div>
          <div>
            <span>META</span>
            <strong>{metaPct !== null ? `${String(metaPct)}%` : '—'}</strong>
          </div>
          <div>
            <span>PREMIAÇÃO</span>
            <strong className="vd-ouro">{cheque ? reaisCurto(cheque.aReceber) : '—'}</strong>
          </div>
        </div>
      </div>

      <div className="pcm-kpis">
        <div className="vd-kpi">
          <div className="vd-kpi-rotulo">ITENS NA SEMANA</div>
          <div className="vd-kpi-valor">{pessoa ? itensSemana : '—'}</div>
        </div>
        <div className="vd-kpi">
          <div className="vd-kpi-rotulo">VALOR VENDIDO</div>
          <div className="vd-kpi-valor">{pessoa ? reaisCurto(valorSemana) : '—'}</div>
        </div>
        <div className="vd-kpi">
          <div className="vd-kpi-rotulo">MÉDIA POR VENDA</div>
          <div className="vd-kpi-valor">
            {pessoa && validas.length ? reaisCurto(valorSemana / validas.length) : '—'}
          </div>
        </div>
        <div className="vd-kpi">
          <div className="vd-kpi-rotulo">CANCELADAS</div>
          <div className="vd-kpi-valor">{pessoa ? canceladas : '—'}</div>
        </div>
      </div>

      <div className="pcm-grade">
        <div className="vd-cartao">
          <div className="vd-cartao-topo">
            <h2>Vendas por dia · 30 dias</h2>
            <span className="vd-legenda">
              <i className="vd-leg-atual" /> Itens <i className="pcm-leg-media" /> Média da equipe
            </span>
          </div>
          {pessoa && equipe && (
            <Barras pessoa={pessoa.porDia} equipe={equipe.porDia} ativos={ativos} />
          )}
        </div>
        <div className="pcm-coluna">
          <div className="vd-cartao pcm-tipos">
            <h2>Premiação por tipo · semana</h2>
            {cheque === undefined ? (
              <p className="vd-cinza">Carregando…</p>
            ) : cheque === null ? (
              <p className="vd-cinza">Sem premiação nesta semana.</p>
            ) : (
              <dl>
                <div>
                  <dt>Vitaminas</dt>
                  <dd>{reaisN(cheque.tipos.vitamina.bruto)}</dd>
                </div>
                <div>
                  <dt>Pré-vencidos</dt>
                  <dd>{reaisN(cheque.tipos.pre_vencido.bruto)}</dd>
                </div>
                <div>
                  <dt>Paradão</dt>
                  <dd>{reaisN(cheque.tipos.paradao.bruto)}</dd>
                </div>
                <div>
                  <dt>Deduzido</dt>
                  <dd className="pcm-vermelho">
                    {cheque.descontado > 0 ? `−${reaisN(cheque.descontado)}` : reaisN(0)}
                  </dd>
                </div>
              </dl>
            )}
          </div>
          <div className="vd-cartao pcm-ultimas">
            <h2>Últimas vendas</h2>
            {(pessoa?.vendas ?? []).slice(0, 6).map((v) => (
              <div key={v.id} className="pcm-venda">
                <span className="vd-mono">{hora(v.registradoEm)}</span>
                <span className="pcm-venda-nome">{v.produto}</span>
                <strong>{reais(v.premioTotal)}</strong>
              </div>
            ))}
            {pessoa && pessoa.vendas.length === 0 && (
              <p className="vd-cinza">Nenhuma venda em 30 dias.</p>
            )}
            <a className="pcm-todas" href={linkDasVendas(accountId, trinta, hoje, hoje)}>
              Ver todas as vendas
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Barras({
  pessoa,
  equipe,
  ativos,
}: {
  pessoa: readonly { dia: string; itens: number }[];
  equipe: readonly { dia: string; itens: number }[];
  ativos: number;
}) {
  const media = equipe.map((d) => d.itens / ativos);
  const max = Math.max(1, ...pessoa.map((p) => p.itens), ...media);
  const L = 600;
  const A = 190;
  const w = L / Math.max(1, pessoa.length);
  const y = (v: number) => A - 16 - (v / max) * (A - 30);
  const linha = media
    .map((m, i) => `${i ? 'L' : 'M'}${(i * w + w / 2).toFixed(1)} ${y(m).toFixed(1)}`)
    .join(' ');
  const ultimos = 7;
  return (
    <svg
      className="vd-svg pcm-svg"
      viewBox={`0 0 ${String(L)} ${String(A)}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Itens por dia e média da equipe"
    >
      {pessoa.map((p, i) => (
        <rect
          key={p.dia}
          x={i * w + w * 0.18}
          width={w * 0.64}
          y={y(p.itens)}
          height={Math.max(0, A - 16 - y(p.itens))}
          className={i >= pessoa.length - ultimos ? 'pcm-barra-recente' : 'pcm-barra'}
        />
      ))}
      <path d={linha} className="pcm-media" />
    </svg>
  );
}
